import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import { setupAppPageBodyBackground } from "../game/gameStageBackground";
import {
  STAGE_ROW_KEY,
  ensureStageRowId,
  ensureStageSessionForUser,
  saveGameStageAndReport,
} from "../../functions/stageReportSync";
import StageTimer from "../game/StageTimer";
import greenBtn from "../img/green_button.png";
import beginDesk from "../img/begin_desk.jpg";
import beginMob from "../img/begin_mob.jpg";
import successDesk from "./stage1/stage1_Desk.jpg";
import successMob from "./stage1/stage1_mob.jpg";
import successSeal from "./stage1/sucess.png";
import {
  MEDAL_CATEGORIES,
  MEDAL_ITEMS,
  MEDAL_DISPLAY_POINTS,
  MEDAL_DISPLAY_UNLOCKS,
  unlockHall1Hotspot,
  markHall1HotspotCompleted,
  getRedirectIfHall1StageDone,
} from "./medalDisplayData";
import {
  validateMatchingPlacements,
  placeItem,
  itemsInCategory,
  unplacedItemIds,
} from "./matchingPuzzleUtils";
import { createDragAutoScroller } from "./dragDropHelpers";
import {
  formatSecondsToClock,
  loadHallElapsedFromDb,
  persistHallElapsedTime,
  readTotalScoreFromStage,
  liveProgressPoints,
} from "./hallTimer";
import "../fonts/breuer-headline.css";
import "./stage1.css";

function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

function shuffleMedals(items) {
  const shuffled = [...items];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

function MedalCard({
  medal,
  lifted,
  wrong,
  compact,
  reverting,
  locked,
  onPointerDown,
}) {
  return (
    <button
      type="button"
      className={`medal-card${lifted ? " medal-card--lifted" : ""}${
        wrong ? " medal-card--wrong" : ""
      }${compact ? " medal-card--compact" : ""}${
        reverting ? " medal-card--revert" : ""
      }${locked ? " medal-card--locked" : ""}`}
      onPointerDown={(e) => {
        if (locked) return;
        onPointerDown?.(e, medal.id);
      }}
      aria-label={`Medal${locked ? " (locked in place)" : ""}`}
      aria-grabbed={lifted ? "true" : "false"}
      aria-disabled={locked ? "true" : undefined}
    >
      <img src={medal.image} alt="" className="medal-card__img" draggable={false} />
    </button>
  );
}

function DragGhost({ medal, x, y, width }) {
  if (!medal) return null;
  return (
    <div
      className="medal-drag-ghost"
      style={{
        left: x,
        top: y,
        width: width ? `${width}px` : undefined,
      }}
      aria-hidden
    >
      <div className="medal-drag-ghost__lift">
        <img src={medal.image} alt="" className="medal-card__img" draggable={false} />
      </div>
    </div>
  );
}

/** Hall Stage 1 — Medal Display matching puzzle */
export default function HallStage1() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { user } = useSelector((s) => s.auth);

  const storedUser = useMemo(() => user || getStoredUser(), [user]);
  const isDemoBypass = useMemo(() => {
    const role = String(storedUser?.role || "").toLowerCase();
    const source = String(storedUser?.source || "").toUpperCase();
    const sessionId = String(storedUser?.sessionId || "");
    return (
      sessionId === "demobypass" ||
      role === "demobypass" ||
      source === "DEMO" ||
      sessionId.startsWith("demo")
    );
  }, [storedUser]);
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  const [elapsedSec, setElapsedSec] = useState(0);
  const [totalScore, setTotalScore] = useState(0);
  const [timerReady, setTimerReady] = useState(false);
  const [placements, setPlacements] = useState(() =>
    Object.fromEntries(MEDAL_ITEMS.map((m) => [m.id, null]))
  );
  const [shuffledMedals] = useState(() => shuffleMedals(MEDAL_ITEMS));
  const [lockedIds, setLockedIds] = useState([]);
  const [drag, setDrag] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const [wrongItemIds, setWrongItemIds] = useState([]);
  const [revertingIds, setRevertingIds] = useState([]);
  const [status, setStatus] = useState("playing");
  const [stageRowId, setStageRowId] = useState(
    () => sessionStorage.getItem(STAGE_ROW_KEY) || ""
  );
  const [errorMsg, setErrorMsg] = useState("");
  const elapsedRef = useRef(0);
  const dragRef = useRef(null);
  const placementsRef = useRef(placements);
  const lockedRef = useRef([]);
  const completingRef = useRef(false);
  const demoRedirectTriggeredRef = useRef(false);
  const autoScrollRef = useRef(null);
  if (!autoScrollRef.current) {
    autoScrollRef.current = createDragAutoScroller();
  }

  const medalById = useMemo(
    () => Object.fromEntries(MEDAL_ITEMS.map((m) => [m.id, m])),
    []
  );

  /** Live stage points from correct drops (full 100 when all medals locked). */
  const liveStagePoints = useMemo(
    () =>
      liveProgressPoints(
        lockedIds.length,
        MEDAL_ITEMS.length,
        MEDAL_DISPLAY_POINTS
      ),
    [lockedIds.length]
  );

  const displayPoints =
    status === "success" ? totalScore : totalScore + liveStagePoints;

  useEffect(() => {
    placementsRef.current = placements;
  }, [placements]);

  useEffect(() => {
    lockedRef.current = lockedIds;
  }, [lockedIds]);

  useEffect(() => {
    dragRef.current = drag;
  }, [drag]);

  useEffect(() => {
    dispatch(setBackButtonUrl("/hall-1"));
  }, [dispatch]);

  useEffect(() => {
    document.body.classList.add("hall1-stage1-page");
    const isSuccess = status === "success";
    const cleanupBg = setupAppPageBodyBackground(document.body, {
      deskForeground: isSuccess ? successDesk : beginDesk,
      mobForeground: isSuccess ? successMob : beginMob,
    });
    return () => {
      cleanupBg?.();
      document.body.classList.remove("hall1-stage1-page");
    };
  }, [status]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      ensureStageSessionForUser(storedUser);
      const { stageId, elapsedSec: sec, stage } = await loadHallElapsedFromDb({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass,
      });
      if (cancelled) return;

      const redirect = getRedirectIfHall1StageDone(stage, 1);
      if (redirect) {
        navigate(redirect, { replace: true });
        return;
      }

      if (stageId) setStageRowId(String(stageId));
      elapsedRef.current = sec;
      setElapsedSec(sec);
      setTotalScore(readTotalScoreFromStage(stage));
      setTimerReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [backendBase, adminToken, storedUser, isDemoBypass, navigate]);

  useEffect(() => {
    if (!timerReady || status === "success") return undefined;
    const tick = window.setInterval(() => {
      setElapsedSec((s) => {
        const next = s + 1;
        elapsedRef.current = next;
        return next;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [timerReady, status]);

  useEffect(() => {
    if (!timerReady) return undefined;
    const save = () => {
      persistHallElapsedTime({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass,
        stageId: stageRowId || sessionStorage.getItem(STAGE_ROW_KEY),
        elapsedSec: elapsedRef.current,
      });
    };
    const id = window.setInterval(save, 15000);
    return () => {
      clearInterval(id);
      if (status === "playing") save();
    };
  }, [
    timerReady,
    backendBase,
    adminToken,
    storedUser,
    isDemoBypass,
    stageRowId,
    status,
  ]);

  const trayIds = useMemo(
    () => unplacedItemIds(placements, shuffledMedals),
    [placements, shuffledMedals]
  );

  const flashRevert = useCallback((itemIds) => {
    setRevertingIds(itemIds);
    window.setTimeout(() => setRevertingIds([]), 420);
  }, []);

  const clearDrag = useCallback(() => {
    autoScrollRef.current?.stop();
    setDrag(null);
    setDropTargetId(null);
    document.body.classList.remove("medal-dragging");
  }, []);

  useEffect(() => () => clearDrag(), [clearDrag]);

  const tryPlaceOnCategory = useCallback(
    (itemId, categoryId, originCategory) => {
      if (lockedRef.current.includes(itemId)) {
        clearDrag();
        return;
      }

      const medal = medalById[itemId];
      if (!medal) {
        clearDrag();
        return;
      }

      if (categoryId !== medal.categoryId) {
        setPlacements((prev) => placeItem(prev, itemId, originCategory));
        setWrongItemIds([itemId]);
        flashRevert([itemId]);
        clearDrag();
        return;
      }

      const isFirstCorrectDrop = lockedRef.current.length === 0;

      setPlacements((prev) => placeItem(prev, itemId, categoryId));
      setLockedIds((prev) =>
        prev.includes(itemId) ? prev : [...prev, itemId]
      );
      setWrongItemIds((ids) => ids.filter((id) => id !== itemId));
      setErrorMsg("");
      clearDrag();

      if (
        isDemoBypass &&
        isFirstCorrectDrop &&
        !demoRedirectTriggeredRef.current
      ) {
        demoRedirectTriggeredRef.current = true;
        setTimeout(() => {
          void Swal.fire({
            icon: "info",
            text: "Thank you for playing. Subscribe to any PLAN to play with your peers.",
            confirmButtonText: "OK",
            allowOutsideClick: false,
            allowEscapeKey: false,
          }).then(() => {
            window.location.assign("https://extramileplay.com/plans");
          });
        }, 400);
      }
    },
    [medalById, clearDrag, flashRevert, isDemoBypass]
  );

  const onPointerDownMedal = useCallback(
    (e, itemId) => {
      if (status === "success" || status === "saving") return;
      if (lockedRef.current.includes(itemId)) return;
      if (e.button != null && e.button !== 0) return;

      const el = e.currentTarget;
      const rect = el.getBoundingClientRect();
      const originCategory = placementsRef.current[itemId] ?? null;

      e.preventDefault();
      el.setPointerCapture?.(e.pointerId);

      setDrag({
        itemId,
        originCategory,
        pointerId: e.pointerId,
        offsetX: e.clientX - rect.left,
        offsetY: e.clientY - rect.top,
        x: rect.left,
        y: rect.top,
        width: rect.width,
      });
      setWrongItemIds([]);
      setErrorMsg("");
      document.body.classList.add("medal-dragging");
      autoScrollRef.current?.update(e.clientY);
    },
    [status]
  );

  useEffect(() => {
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d || e.pointerId !== d.pointerId) return;

      autoScrollRef.current?.update(e.clientY);

      setDrag((prev) =>
        prev
          ? {
              ...prev,
              x: e.clientX - prev.offsetX,
              y: e.clientY - prev.offsetY,
            }
          : prev
      );

      const el = document.elementFromPoint(e.clientX, e.clientY);
      const caseEl = el?.closest?.("[data-medal-case]");
      setDropTargetId(caseEl?.getAttribute("data-medal-case") || null);
    };

    const onUp = (e) => {
      const d = dragRef.current;
      if (!d || e.pointerId !== d.pointerId) return;

      autoScrollRef.current?.stop();

      const el = document.elementFromPoint(e.clientX, e.clientY);
      const caseEl = el?.closest?.("[data-medal-case]");
      const trayEl = el?.closest?.("[data-medal-tray]");

      if (caseEl) {
        tryPlaceOnCategory(
          d.itemId,
          caseEl.getAttribute("data-medal-case"),
          d.originCategory
        );
      } else if (trayEl) {
        if (lockedRef.current.includes(d.itemId)) {
          setPlacements((prev) => placeItem(prev, d.itemId, d.originCategory));
          clearDrag();
          return;
        }
        setPlacements((prev) => placeItem(prev, d.itemId, null));
        setWrongItemIds((ids) => ids.filter((id) => id !== d.itemId));
        setErrorMsg("");
        clearDrag();
      } else {
        setPlacements((prev) => placeItem(prev, d.itemId, d.originCategory));
        flashRevert([d.itemId]);
        clearDrag();
      }
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      autoScrollRef.current?.stop();
      clearDrag();
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [tryPlaceOnCategory, clearDrag, flashRevert]);

  const completePuzzle = useCallback(async () => {
    if (completingRef.current) return;
    if (status === "success" || status === "saving") return;
    completingRef.current = true;

    setWrongItemIds([]);
    setErrorMsg("");
    setStatus("saving");

    const timeStr = formatSecondsToClock(elapsedRef.current);
    const answers = {
      puzzle: "medal_display",
      placements: placementsRef.current,
    };

    try {
      let stageId = stageRowId || sessionStorage.getItem(STAGE_ROW_KEY);
      if (!stageId && !isDemoBypass && backendBase && adminToken) {
        stageId = await ensureStageRowId({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
        });
        if (stageId) setStageRowId(String(stageId));
      }

      if (!isDemoBypass && backendBase && adminToken && stageId) {
        const result = await saveGameStageAndReport({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
          stageId,
          stageNumber: 1,
          score: MEDAL_DISPLAY_POINTS,
          answers,
          time: timeStr,
        });
        if (result?.total_score != null) {
          setTotalScore(Number(result.total_score) || 0);
        } else {
          setTotalScore((prev) => prev + MEDAL_DISPLAY_POINTS);
        }
      } else {
        setTotalScore((prev) => prev + MEDAL_DISPLAY_POINTS);
      }

      markHall1HotspotCompleted("medal_display");
      unlockHall1Hotspot(MEDAL_DISPLAY_UNLOCKS);
      setStatus("success");
    } catch (err) {
      console.error("Medal display save:", err);
      completingRef.current = false;
      setStatus("playing");
      setErrorMsg("Could not save progress. Please try again.");
    }
  }, [
    status,
    stageRowId,
    isDemoBypass,
    backendBase,
    adminToken,
    storedUser,
  ]);

  useEffect(() => {
    if (status !== "playing") return;
    const result = validateMatchingPlacements(placements, MEDAL_ITEMS);
    if (result.ok) {
      completePuzzle();
    }
  }, [placements, status, completePuzzle]);

  const dragMedal = drag ? medalById[drag.itemId] : null;

  return (
    <div className="hall1-stage1 medal-gallery">
      <div className="stage-escape-hud">
        <StageTimer
          timeLabel={formatSecondsToClock(elapsedSec)}
          points={displayPoints}
        />
      </div>

      <div className="medal-gallery__inner">
        <header className="medal-gallery__header">
          <h1 className="medal-gallery__title">MEDAL GALLERY</h1>
          <p className="medal-gallery__subtitle">
          The medals have been mixed up. Sort them into the correct sporting events to restore the display.
          </p>
        </header>

        <div className="medal-gallery__cases" role="list">
          {MEDAL_CATEGORIES.map((cat) => {
            const placedIds = itemsInCategory(placements, cat.id);
            const isOver = dropTargetId === cat.id && drag;
            return (
              <div
                key={cat.id}
                role="listitem"
                data-medal-case={cat.id}
                className={`medal-case${isOver ? " medal-case--over" : ""}`}
              >
                <img
                  src={cat.image}
                  alt={cat.label}
                  className="medal-case__frame"
                  draggable={false}
                />
                <div className="medal-case__slots">
                  {placedIds.map((id) => (
                    <MedalCard
                      key={id}
                      medal={medalById[id]}
                      compact
                      locked={lockedIds.includes(id)}
                      lifted={drag?.itemId === id}
                      wrong={wrongItemIds.includes(id)}
                      reverting={revertingIds.includes(id)}
                      onPointerDown={onPointerDownMedal}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="medal-gallery__divider" aria-hidden>
          <span>Drag &amp; Drop Medals</span>
        </div>

        <div className="medal-gallery__tray" data-medal-tray="1">
          {trayIds.map((id) => (
            <MedalCard
              key={id}
              medal={medalById[id]}
              locked={lockedIds.includes(id)}
              lifted={drag?.itemId === id}
              wrong={wrongItemIds.includes(id)}
              reverting={revertingIds.includes(id)}
              onPointerDown={onPointerDownMedal}
            />
          ))}
          {trayIds.length === 0 ? (
            <p className="medal-gallery__tray-empty">All medals placed</p>
          ) : null}
        </div>

        {errorMsg ? (
          <p className="medal-gallery__error" role="alert">
            {errorMsg}
          </p>
        ) : null}

        {status === "saving" ? (
          <p className="medal-gallery__saving" aria-live="polite">
            Restoring display…
          </p>
        ) : null}
      </div>

      {drag && dragMedal ? (
        <DragGhost
          medal={dragMedal}
          x={drag.x}
          y={drag.y}
          width={drag.width}
        />
      ) : null}

      {status === "success" ? (
        <div className="medal-complete" role="dialog" aria-modal="true">
          <div className="medal-complete__veil" aria-hidden />

          <div className="medal-complete__content">
            <header className="medal-complete__header">
              <h2 className="medal-complete__title">PUZZLE COMPLETE!</h2>
              <p className="medal-complete__subtitle">
                Perfect! The Medal Display is restored.
              </p>
              <p className="medal-complete__unlock">
                Great work! The vintage commentary radio is working again.
              </p>
              <p className="medal-complete__unlock medal-complete__unlock--soft">
                Let&apos;s see what it has to tell us.
              </p>
            </header>

            <div className="medal-complete__seal">
              <img
                src={successSeal}
                alt=""
                className="medal-complete__seal-img"
                draggable={false}
              />
            </div>

            <div className="medal-complete__footer">
              <div className="medal-complete__panel medal-complete__panel--stats">
                <div className="medal-complete__stat">
                  <span className="medal-complete__stat-icon" aria-hidden>
                    ⏱
                  </span>
                  <span>{formatSecondsToClock(elapsedSec)}</span>
                </div>
                <div className="medal-complete__stat">
                  <span className="medal-complete__stat-icon" aria-hidden>
                    ★
                  </span>
                  <span>{MEDAL_DISPLAY_POINTS}</span>
                </div>
                <p className="medal-complete__panel-foot">REWARD EARNED</p>
              </div>

              <button
                type="button"
                className="medal-complete__continue"
                style={{ backgroundImage: `url(${greenBtn})` }}
                onClick={() => navigate("/hall-1")}
              >
                CONTINUE
              </button>

              <div className="medal-complete__panel medal-complete__panel--medals">
                <p className="medal-complete__panel-head">
                  ALL CATEGORIES RESTORED
                </p>
                <div className="medal-complete__medals">
                  {MEDAL_ITEMS.slice(0, 5).map((m) => (
                    <img
                      key={m.id}
                      src={m.image}
                      alt=""
                      className="medal-complete__medal-thumb"
                    />
                  ))}
                </div>
                <p className="medal-complete__panel-note">
                  Great job, Conservator!
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
